import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Banner, Button, Field, Page, Spinner } from "../../components";
import { AuthLayout } from "./AuthLayout";
import { ApiError, describeApiError } from "../../api/errors";
import { cleanCode, verifyMfa } from "../../api/mfa";
import { useSession } from "../../api/session";
import { t } from "../../i18n/t";
import { useFinishSignIn } from "./mfaFlow";

/**
 * xmg-sec-mfa (S3a): `/auth/mfa`, the second half of every sign-in for an account that has a factor. The password
 * was accepted; this screen asks for the six-digit code from the authenticator app, or one of the recovery codes,
 * and then continues to wherever the sign-in started. It is outside `RequireSession` (which would send it here in a
 * loop) and guards itself: anonymous goes to sign-in, an already-verified or operator-key session goes on.
 */
export function MfaChallengeScreen(): JSX.Element {
  const { status, mfa, markMfaVerified } = useSession();
  const { target, finish } = useFinishSignIn();
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === "loading") return <Spinner />;
  if (status === "anonymous") return <Navigate to="/auth/login" replace state={{ from: target.from }} />;
  if (mfa === "enrollment_required") return <Navigate to="/auth/mfa/setup" replace state={target} />;
  if (mfa !== "required") return <Navigate to={target.from} replace />;

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyMfa(useRecovery ? { recovery_code: code.trim() } : { code: cleanCode(code) });
      markMfaVerified();
      finish();
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("rate_limited")) {
        const seconds = caught.details.retry_after_seconds;
        setError(t("mfa.challenge.throttled", { seconds: typeof seconds === "number" ? seconds : 60 }));
      } else if (caught instanceof ApiError && caught.is("mfa_locked")) {
        setError(t("mfa.challenge.locked"));
      } else if (caught instanceof ApiError && caught.is("mfa_invalid_code")) {
        setError(useRecovery ? t("mfa.challenge.recovery_invalid") : t("mfa.challenge.invalid"));
      } else {
        setError(describeApiError(caught, t("error.unexpected")));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <Page narrow eyebrow={t("mfa.eyebrow")} title={t("mfa.challenge.title")}>
        <p>{useRecovery ? t("mfa.challenge.recovery_intro") : t("mfa.challenge.intro")}</p>
        <form onSubmit={(event) => void onSubmit(event)} className="authform">
          <Field
            label={useRecovery ? t("mfa.challenge.recovery_label") : t("mfa.challenge.code_label")}
            error={error}
          >
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                inputMode={useRecovery ? "text" : "numeric"}
                autoComplete="one-time-code"
                autoFocus
                className="mono"
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            )}
          </Field>
          <Button
            type="submit"
            variant="primary"
            className="button--auth"
            disabled={busy || code.trim() === ""}
          >
            {t("mfa.challenge.submit")}
          </Button>
          <Button
            onClick={() => {
              setUseRecovery((prev) => !prev);
              setCode("");
              setError(null);
            }}
          >
            {useRecovery ? t("mfa.challenge.use_app") : t("mfa.challenge.use_recovery")}
          </Button>
          {useRecovery ? <Banner tone="info">{t("mfa.challenge.lost_both")}</Banner> : null}
        </form>
      </Page>
    </AuthLayout>
  );
}
